import { Button, Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { Plus } from 'react-bootstrap-icons';
import { useIsFetching, useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  DomainRegistrarAccountFormFields,
  DomainRegistrarAccountsFormInputs,
  DomainRegistrarAccountsQueryKeys,
  useCreateDomainRegistrarAccountsMutation
} from '@/modules/DomainRegistrars';

import { DomainRegistrarAccountsFormView } from './DomainRegistrarAccountsFormView';
import { domainRegistrarAccountFormValidationSchema } from './validation';

interface Props {
  domainRegistrarId: string;
}

export const DomainRegistrarAccountsCreateFormContainer: FC<Props> = memo(({ domainRegistrarId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainRegistrarAccountFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainRegistrarAccountsFormInputs>>(null);
  const [isCreateAccountFormVisible, setIsCreateAccountFormVisible] = useState(false);

  const { mutate: createDomainRegistrarAccountMutation, isLoading: createDomainRegistrarAccountIsLoading } =
    useCreateDomainRegistrarAccountsMutation(domainRegistrarId)({
      onSuccess: async () => {
        await queryClient.refetchQueries([DomainRegistrarAccountsQueryKeys.GetAll, domainRegistrarId], {
          exact: true
        });
        handleBack();
      },
      onError: (err) => {
        setFormErrors(extractFormErrors<typeof DomainRegistrarAccountsFormInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: DomainRegistrarAccountFormFields = { login: undefined, password: undefined };

  const isDomainRegistrarAccountsListFetching = !!useIsFetching({
    queryKey: [DomainRegistrarAccountsQueryKeys.GetAll, domainRegistrarId],
    exact: true
  });

  const isDomainRegistrarAccountsListError = !!queryClient.getQueryState([DomainRegistrarAccountsQueryKeys.GetAll])
    ?.fetchFailureCount;

  const handleFinish = useCallback((values: DomainRegistrarAccountFormFields) => {
    setFormErrors(null);
    createDomainRegistrarAccountMutation(values);
  }, []);

  const handleBack = useCallback(() => {
    form.resetFields();
    setIsCreateAccountFormVisible(false);
  }, []);

  const handleFinishFailed = useCallback(() => {
    setFormErrors(null);
  }, []);

  const handleShowAccountForm = useCallback(() => {
    setIsCreateAccountFormVisible(true);
  }, []);

  return (
    <>
      { isCreateAccountFormVisible ? (
        <DomainRegistrarAccountsFormView
          form={ form }
          formName={ 'domain-registrar-account-create' }
          formType={ 'create' }
          initialValues={ initialValues }
          isFormLoading={ createDomainRegistrarAccountIsLoading }
          validationSchema={ domainRegistrarAccountFormValidationSchema }
          formErrors={ formErrors }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) : (
        <Button
          block
          type="dashed"
          disabled={ isDomainRegistrarAccountsListFetching || isDomainRegistrarAccountsListError }
          icon={ <Plus/> }
          onClick={ handleShowAccountForm }
        >
          Додати акаунт
        </Button>
      ) }
    </>
  );
});
